import { z } from 'zod';
/* ==========================================================================
 *  Health-data integration shapes (ADR-0027 / T14.2 / ISSUE-075)
 *
 *  Apple HealthKit (iOS) + Health Connect (Android) import of weight + steps,
 *  and export of daily nutrition totals. Browser/native-safe (zod-only).
 *
 *  THE INVARIANT (ADR-0027 §2): imported steps are CONTEXT for training/rest
 *  tagging only. They must NEVER reach the expenditure engine (ADR-0026).
 *  `ImportedActivityDay` deliberately carries no energy field.
 * ========================================================================== */
/** The platform health store a sample came from. Web/PWA has no native source. */
export const healthSourcePlatformSchema = z.enum(['apple_health', 'health_connect']);
export const healthLinkStatusSchema = z.enum(['connected', 'disconnected']);
/** Per-channel scopes the user granted on connect. We only ever request these three. */
export const healthScopesSchema = z
    .object({
    weightRead: z.boolean().default(false),
    stepsRead: z.boolean().default(false),
    nutritionWrite: z.boolean().default(false),
})
    .strict();
/**
 * The consent purpose recorded in `consent_event` for health-data processing.
 * NOTE (ADR-0027 deviation, see ADR §4 note): health_sync is intentionally NOT
 * added to the generic `consentPurposeSchema` (which drives the /me/consents
 * privacy toggles) — that would let the generic PUT bypass the per-source read
 * gate (`health_link.status`). It is written directly to `consent_event` by the
 * connect/disconnect flow, which atomically flips the source's read gate.
 */
export const HEALTH_CONSENT_PURPOSE = 'health_sync';
/* ----- Import (read from the platform store) ----------------------------- */
/** One imported weigh-in. Mirrors createWeightSchema bounds; kg is canonical. */
export const importedWeightSchema = z
    .object({
    measuredAt: z.string().datetime({ offset: true }),
    weightKg: z.number().positive().max(700),
})
    .strict();
/** One imported day of steps. CONTEXT ONLY — note the absence of any kcal field. */
export const importedActivityDaySchema = z
    .object({
    localDate: z.string().date(),
    steps: z.number().int().nonnegative().max(500_000),
})
    .strict();
/** Ingest request: a sync window of weight + activity from one source. */
export const healthImportRequestSchema = z
    .object({
    sourcePlatform: healthSourcePlatformSchema,
    weights: z.array(importedWeightSchema).max(1000).default([]),
    activity: z.array(importedActivityDaySchema).max(400).default([]),
    /** Optional per-channel high-water mark for the next incremental pull. */
    cursor: z.record(z.string(), z.string()).optional(),
    timezone: z.string().trim().min(1).max(100).optional(),
})
    .strict();
export const healthImportResponseSchema = z.object({
    /** Weigh-ins written to body_metric(source='health_import'). */
    importedWeights: z.number().int().nonnegative(),
    /** Weigh-ins skipped as duplicates of an existing same-day weigh-in (dedup, ADR §6). */
    skippedWeights: z.number().int().nonnegative(),
    /** Daily step rows upserted into health_activity_day. */
    importedActivityDays: z.number().int().nonnegative(),
    lastSyncAt: z.string().datetime(),
});
/* ----- Export (write to the platform store) ------------------------------ */
/** Daily nutrition totals pushed to the platform store (opt-in, write-only). */
export const nutritionExportSampleSchema = z.object({
    localDate: z.string().date(),
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative(),
    carbsG: z.number().nonnegative(),
    fatG: z.number().nonnegative(),
    fiberG: z.number().nonnegative(),
});
/* ----- Connection state (health_link) ------------------------------------ */
export const connectHealthLinkSchema = z
    .object({
    sourcePlatform: healthSourcePlatformSchema,
    scopes: healthScopesSchema,
})
    .strict();
export const healthLinkStateSchema = z.object({
    sourcePlatform: healthSourcePlatformSchema,
    status: healthLinkStatusSchema,
    scopes: healthScopesSchema.nullable(),
    connectedAt: z.string().datetime(),
    disconnectedAt: z.string().datetime().nullable(),
    lastSyncAt: z.string().datetime().nullable(),
    /** Per-channel high-water mark for the next incremental pull (null until first sync). */
    lastSyncCursor: z.record(z.string(), z.string()).nullable(),
});
export const healthLinksResponseSchema = z.object({
    links: z.array(healthLinkStateSchema),
});
//# sourceMappingURL=health-integration.js.map